import moment from "moment";
import i18n from "../i18n";
import crypto from "crypto";
import turkishToEnglish from "../constants/turkishToEnglish";
import {
    androidDeviceModelIdentifiers,
    deviceOSEnum,
    iOSDeviceModelIdentifiers,
    IMAGE_EXTENSIONS
} from "constants/constants";

export const b64EncodeUnicode = str => {
    return btoa(
        encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function toSolidBytes(match, p1) {
            return String.fromCharCode("0x" + p1);
        })
    );
};

export const normalizeStringSearchFilter = (str = "") =>
    str
        ?.toLowerCase()
        ?.normalize("NFD") // Unicode normalizasyonu
        ?.replace(/[\u0300-\u036f]/g, "") // aksan ve özel karakterleri kaldırır
        ?.replace(/ç/g, "c")
        ?.replace(/ğ/g, "g")
        ?.replace(/ı/g, "i")
        ?.replace(/ö/g, "o")
        ?.replace(/ş/g, "s")
        ?.replace(/ü/g, "u");

export const b64DecodeToUrl = async (b64, mimeType) => {
    const byteCharacters = atob(b64);
    const byteNumbers = await new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = await new Uint8Array(byteNumbers);
    const blob = mimeType
        ? new Blob([byteArray], {type: mimeType})
        : new Blob([byteArray]);

    const url = await window.URL.createObjectURL(blob);
    return url;
};

export const b64DecodeToCsvUrl = async b64 => {
    const byteCharacters = atob(b64);
    const byteNumbers = await new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = await new Uint8Array(byteNumbers);
    const blob = await new Blob([byteArray], {type: "text/csv;charset=utf-8"});
    return window.URL.createObjectURL(blob);
};

export const formatPhoneNumber = (str, placeholder = "-") => {
    const cleaned = ("" + str).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return match[1] + "(" + match[2] + ") " + match[3] + "-" + match[4];
    }
    return placeholder;
};

export const formatMomentDate = date => {
    return date ? moment(date).format("DD-MM-YYYY HH:mm:ss") : "";
};

export const constantFind = (array, value) => {
    const found = array && array.length && array.find(arr => arr.value === value);
    return found ? found.name : "-";
};

export const isConstantExist = (array, value, fieldName = "value") => {
    const found = array && array.length && array.find(arr => arr[fieldName] === value);
    return !!found;
};

/** Örn. paymentAuthorizedUsers: kullanıcı id’si listede mi (string/ObjectId karşılaştırması) */
export const isUserIdInList = (userId, ids) =>
    userId != null &&
    Array.isArray(ids) &&
    ids.length > 0 &&
    ids.some(id => String(id) === String(userId));

export const getConstant = (array, value) => {
    const found = array && array.length && array.find(arr => arr.value === value);
    return found;
};

export const arrayAlphabeticalSort = (array, key = "name") => {
    const sorted = Array.isArray(array)
        ? array.sort((a, b) => {
              return a[key]
                  ? ["tümü", "all"].includes(a[key].toLowerCase()) ||
                    ["tümü", "all"].includes(b[key].toLowerCase())
                      ? 1
                      : a[key].localeCompare(b[key], "tr")
                  : 1;
          })
        : [];
    return sorted;
};

export const arrayNaturalSort = (array, key = "name") => {
    const sorted = Array.isArray(array)
        ? array.sort((a, b) =>
              a[key].localeCompare(b[key], undefined, {
                  numeric: true,
                  sensitivity: "base"
              })
          )
        : [];
    return sorted;
};

export const intIncSort = array => {
    const sorted = Array.isArray(array)
        ? [...array].sort((prev, curr) => prev.index - curr.index)
        : [];
    return sorted;
};

export const calculateDuration = ({
    startDate,
    endDate,
    dayUnit = "g",
    hourUnit = "s",
    minuteUnit = "dk",
    secondUnit = "sn"
}) => {
    const duration = Math.abs(new Date(startDate) - new Date(endDate));
    const seconds = Math.floor((duration / 1000) % 60),
        minutes = Math.floor((duration / (1000 * 60)) % 60),
        hours = Math.floor((duration / (1000 * 60 * 60)) % 24),
        days = Math.floor((duration / (1000 * 60 * 60 * 24)) % 30);
    return `${days ? days + dayUnit + " " : ""}${hours ? hours + hourUnit + " " : ""}${
        minutes ? minutes + minuteUnit + " " : ""
    }${seconds || seconds === 0 ? seconds + secondUnit + " " : ""}`;
};

export const decodePolyline = encoded => {
    const points = [];
    let index = 0,
        len = encoded.length;
    let lat = 0,
        lng = 0;
    while (index < len) {
        var b,
            shift = 0,
            result = 0;
        do {
            b = encoded.charAt(index++).charCodeAt(0) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);

        const dlat = (result & 1) != 0 ? ~(result >> 1) : result >> 1;
        lat += dlat;
        shift = 0;
        result = 0;
        do {
            b = encoded.charAt(index++).charCodeAt(0) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);
        const dlng = (result & 1) != 0 ? ~(result >> 1) : result >> 1;
        lng += dlng;

        points.push({lat: lat / 1e5, lng: lng / 1e5});
    }
    return points;
};

export const diffObject = (obj1, obj2) => {
    if (!obj1 || !obj2) return {};
    const diff = {};
    const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

    for (const key of allKeys) {
        const val1 = obj1[key];
        const val2 = obj2[key];

        if (val1 !== val2) {
            diff[key] = val2;
        }
    }

    return diff;
};

export const generateSecurePassword = (length = 12) => {
    if (length <= 3) {
        throw new Error("Password length must be greater than 3");
    }
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const digits = "0123456789";
    const specialCharacter = "#$%&'()*+,-./:;<=>?@[]^_`{|}~";
    const all = lower + upper + digits + specialCharacter;

    function getRandomInt(max) {
        const randomBytes = crypto.randomBytes(4);
        const randomNum = randomBytes.readUInt32BE(0);
        return randomNum % max;
    }

    function getRandomChar(charset) {
        return charset[getRandomInt(charset.length)];
    }

    function shuffleArray(array) {
        for (let i = array.length - 1; i > 0; i--) {
            const j = getRandomInt(i + 1);
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    }

    let password = [
        getRandomChar(lower),
        getRandomChar(upper),
        getRandomChar(digits),
        getRandomChar(specialCharacter)
    ];

    while (password.length <= length) {
        password.push(getRandomChar(all));
    }

    password = shuffleArray(password);

    return password.join("");
};

export const averageGeolocation = coords => {
    if (coords.length === 1) {
        return coords[0];
    }

    let x = 0.0;
    let y = 0.0;
    let z = 0.0;

    for (let coord of coords) {
        let latitude = (coord.latitude * Math.PI) / 180;
        let longitude = (coord.longitude * Math.PI) / 180;

        x += Math.cos(latitude) * Math.cos(longitude);
        y += Math.cos(latitude) * Math.sin(longitude);
        z += Math.sin(latitude);
    }

    let total = coords.length;

    x = x / total;
    y = y / total;
    z = z / total;

    let centralLongitude = Math.atan2(y, x);
    let centralSquareRoot = Math.sqrt(x * x + y * y);
    let centralLatitude = Math.atan2(z, centralSquareRoot);

    return {
        lat: (centralLatitude * 180) / Math.PI,
        lng: (centralLongitude * 180) / Math.PI
    };
};

export const handleCheckBoxText = (searchText, city) => {
    let text = "";
    if (city && city !== "all") {
        text = city[0].toUpperCase() + city.slice(1);
        text = text.concat(" " + i18n.t("helpers.handleCheckBoxText.city", {ns: "common"}));
        if (searchText !== "") {
            text = i18n.t("helpers.handleCheckBoxText.applyToAllSubcompaniesFilteredWith", {
                ns: "common",
                searchText: text + ", " + searchText
            });
        } else {
            text = i18n.t("helpers.handleCheckBoxText.applyToSubcompaniesFilteredWith", {
                ns: "common",
                text: text
            });
        }
    } else {
        if (searchText !== "") {
            text = i18n.t("helpers.handleCheckBoxText.applyToAllSubcompaniesFilteredWith", {
                ns: "common",
                searchText: searchText
            });
        } else {
            text = i18n.t("helpers.handleCheckBoxText.applyAllSubcompanies", {ns: "common"});
        }
    }

    return text;
};

export const isEmptyObject = obj => {
    if (typeof obj === "object" && obj != null && Object.keys(obj).length !== 0) {
        return false;
    } else {
        return true;
    }
};

export const removeNullEntries = params => {
    let queryParams = {};
    Object.entries(params)
        .filter(
            entry =>
                !(
                    entry[1] === null ||
                    entry[1] === "" ||
                    entry[1] === undefined ||
                    (Array.isArray(entry[1]) && entry[1].length === 0)
                )
        )
        .forEach(entry => {
            queryParams[entry[0]] = entry[1];
        });
    return queryParams;
};

export const toMinute = second => {
    return (second / 60).toFixed(2);
};

export const minuteToString = (minutes, showMinuteShort = true) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
        return `${hours} ${i18n.t("units.hour.short", {
            ns: "common"
        })} ${remainingMinutes} ${i18n.t("units.minute.short", {ns: "common"})}`;
    } else {
        return `${remainingMinutes} ${
            showMinuteShort ? i18n.t("units.minute.short", {ns: "common"}) : ""
        }`;
    }
};

export const minuteToStringForAddShift = (minutes, showMinuteShort = true) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
        return `${hours} ${
            remainingMinutes !== 0
                ? i18n.t("units.hour.short", {
                      ns: "common"
                  })
                : i18n.t("units.hour.long", {
                      ns: "common"
                  })
        } ${remainingMinutes !== 0 ? remainingMinutes : ""} ${
            remainingMinutes !== 0 ? i18n.t("units.minute.short", {ns: "common"}) : ""
        }`;
    } else {
        return `${remainingMinutes} ${
            showMinuteShort ? i18n.t("units.minute.long", {ns: "common"}) : ""
        }`;
    }
};

export const encodeUriAll = value => {
    return value.replace(
        /[^A-Za-z0-9]/g,
        c =>
            `%${c
                .charCodeAt(0)
                .toString(16)
                .toUpperCase()}`
    );
};

export const truncateString = (str, num) => {
    // Check if str is undefined, null, or not a string
    if (!str || typeof str !== "string") {
        return "-";
    }

    // If the length of str is less than or equal to num
    // just return str--don't truncate it.
    if (str.length <= num) {
        return str;
    }
    // Return str truncated with '...' concatenated to the end of str.
    return str.slice(0, num) + "...";
};

export const normalizeString = str => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};
export const compareString = (str = "", searchStr = "") => {
    return normalizeString(str)
        .toLowerCase()
        .includes(normalizeString(searchStr).toLowerCase());
};

export const getReadableTextColor = color => {
    if (!color || typeof color !== "string") return "#1f1f1f";

    const sanitized = color.replace("#", "");

    const expandHex = value =>
        value
            .split("")
            .map(char => char + char)
            .join("");

    const normalized =
        sanitized.length === 3 ? expandHex(sanitized) : sanitized.length === 6 ? sanitized : null;

    if (!normalized) return "#1f1f1f";

    const r = parseInt(normalized.substring(0, 2), 16);
    const g = parseInt(normalized.substring(2, 4), 16);
    const b = parseInt(normalized.substring(4, 6), 16);

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance > 0.6 ? "#1f1f1f" : "#ffffff";
};

export const attachMarkerPopupInteractions = marker => {
    if (!marker || typeof marker.on !== "function") return;

    const openPopup = () => marker.openPopup();
    const closePopup = () => marker.closePopup();

    marker.on("mouseover", openPopup);
    marker.on("mouseout", closePopup);
    marker.on("click", openPopup);
};

export const getLocaleLang = ({resolvedLanguage = "tr"}) =>
    resolvedLanguage === "en" ? "en" : "tr";

export const compareArrays = (array1, array2) => {
    for (let item of array1) {
        if (!array2.includes(item)) {
            return false;
        }
    }
    return true;
};

export const generateRandomOrderNumber = () => {
    return moment().format("mmssSSS");
};

export const calculateElapsedTime = (startDateString, endDateString) => {
    const startDate = moment(startDateString);
    const endDate = moment(endDateString);

    const diff = moment.duration(endDate.diff(startDate));

    const hours = Math.floor(diff.asHours());
    const minutes = Math.floor(diff.asMinutes()) % 60;
    const seconds = diff.seconds();

    const elapsedTime = `${hours}:${minutes}:${seconds}`;

    return elapsedTime;
};

export const toTurkishLowerCase = str => {
    return str
        .replace(/I/g, "ı")
        .replace(/İ/g, "i")
        .toLocaleLowerCase("tr");
};

export const toTurkishUpperCase = str => {
    return str
        .replace(/ı/g, "I")
        .replace(/i/g, "İ")
        .toLocaleUpperCase("tr");
};

export const formatNumberString = number => {
    if (number === undefined || number === null) return "####";
    const convertedNumber = number.toString().replace(",", ".");
    if (isNaN(Number(convertedNumber))) return "####";
    const fixedNumber = Number(convertedNumber).toFixed(2);
    return Number(fixedNumber).toLocaleString("tr-TR");
};

export const isNumeric = value => {
    return !isNaN(parseInt(value)) && !isNaN(parseFloat(value)) && isFinite(value);
};

export const normalizeText = text => {
    if (!text) return "";

    let normalized = text.toLowerCase();

    normalized = turkishToEnglish(normalized);

    normalized = normalized.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    return normalized;
};

export const getModelHumanReadable = (model, osName) => {
    let modelName = model;
    if (!model || !osName) {
        return modelName;
    }

    if (osName === deviceOSEnum.IOS || osName === "ios") {
        const modelPartsRegex = /(iPhone\d+,\d+)/;
        const match = model.match(modelPartsRegex);

        if (match) {
            const modelName = match[1];
            return `${iOSDeviceModelIdentifiers[modelName]}`;
        }
    }

    if (osName === deviceOSEnum.ANDROID) {
        const androidModelName = androidDeviceModelIdentifiers[model];
        if (androidModelName) {
            modelName = androidModelName;
        }
    }

    return modelName;
};

/**
 * Leaflet marker icon konfigürasyonunu yapar
 * Bu fonksiyon Leaflet'in default marker icon'larını CDN'den yükler
 * @param {Object} L - Leaflet kütüphanesi
 * @returns {boolean} Konfigürasyon başarılı olup olmadığını döner
 */
export const configureLeafletMarkerIcons = L => {
    if (!L || !L.Icon || !L.Icon.Default) {
        console.warn("Leaflet kütüphanesi yüklenmemiş veya Icon.Default mevcut değil");
        return false;
    }

    try {
        // Fix for default markers in Leaflet
        if (L.Icon.Default.prototype && L.Icon.Default.prototype._getIconUrl) {
            delete L.Icon.Default.prototype._getIconUrl;
        }

        if (L.Icon.Default.mergeOptions) {
            L.Icon.Default.mergeOptions({
                iconRetinaUrl:
                    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
                iconUrl:
                    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
                shadowUrl:
                    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png"
            });
        }
        return true;
    } catch (error) {
        return false;
    }
};

/**
 * Leaflet tile layer türleri
 */
export const TILE_LAYER_TYPES = {
    BRIGHT: "bright",
    BASIC: "basic",
    OSM: "osm"
};

/**
 * Tile layer URL'lerini döner
 * @param {string} type - Tile layer türü (bright, basic, osm)
 * @returns {string} Tile layer URL
 */
export const getTileLayerUrl = type => {
    switch (type) {
        case TILE_LAYER_TYPES.BRIGHT:
            return "https://tile-server.vigo.ist/styles/bright/512/{z}/{x}/{y}.png";
        case TILE_LAYER_TYPES.BASIC:
            return "https://tile-server.vigo.ist/styles/basic/512/{z}/{x}/{y}.png";
        default:
            return "https://tile-server.vigo.ist/styles/basic/512/{z}/{x}/{y}.png";
    }
};

/**
 * Local storage'dan tile layer tercihini okur
 * @returns {string} Tile layer türü
 */
export const getTileLayerPreference = () => {
    try {
        return localStorage.getItem("leaflet_tile_layer") || TILE_LAYER_TYPES.BASIC;
    } catch (error) {
        return TILE_LAYER_TYPES.BASIC;
    }
};

/**
 * Local storage'a tile layer tercihini kaydeder
 * @param {string} type - Tile layer türü
 */
export const saveTileLayerPreference = type => {
    try {
        localStorage.setItem("leaflet_tile_layer", type);
    } catch (error) {
        console.warn("Tile layer tercihi kaydedilemedi:", error);
    }
};

/**
 * Leaflet tile layer ekler (fallback yok - sadece tercih edilen tile server kullanılır)
 * @param {Object} L - Leaflet kütüphanesi
 * @param {Object} map - Leaflet map instance
 * @param {string} preferredType - Tercih edilen tile layer türü
 * @returns {Object} Tile layer instance veya null
 */
export const addTileLayerWithFallback = (L, map, preferredType = TILE_LAYER_TYPES.BASIC) => {
    if (!L || !map) {
        console.warn("Leaflet kütüphanesi veya map instance bulunamadı");
        return null;
    }

    const tileLayerUrl = getTileLayerUrl(preferredType);
    try {
        const tileLayer = L.tileLayer(tileLayerUrl, {
            maxZoom: 22,
            maxNativeZoom: 19,
            className: "map-tiles-enhanced",
            tileSize: 512,
            zoomOffset: -1
        });

        tileLayer.on("tileerror", error => {
            console.warn(`Tile yükleme hatası: ${tileLayerUrl}`, error);
        });

        tileLayer.addTo(map);
        return tileLayer;
    } catch (error) {
        console.error("Tile layer eklenirken kritik hata:", error);
        return null;
    }
};

/**
 * Türkiye harita sınırları (sıkı sınırlar - minimum deniz alanı)
 */
export const TURKEY_BOUNDS = {
    southWest: [36.0, 25.8],
    northEast: [42.1, 44.8]
};

/**
 * Leaflet map oluşturur ve temel konfigürasyonu yapar
 * @param {Object} L - Leaflet kütüphanesi
 * @param {HTMLElement} mapContainer - Map container elementi
 * @param {Object} center - Map merkezi {lat, lng}
 * @param {number} zoom - Zoom seviyesi
 * @param {Object} options - Ek seçenekler
 * @param {boolean} options.restrictToTurkey - Haritayı Türkiye sınırları ile sınırla (varsayılan: true)
 * @returns {Object|null} Leaflet map instance
 */
export const createLeafletMap = (L, mapContainer, center, zoom = 13, options = {}) => {
    if (!L || !mapContainer) {
        console.warn("Leaflet kütüphanesi veya map container mevcut değil");
        return null;
    }

    if (!center || typeof center.lat !== "number" || typeof center.lng !== "number") {
        return null;
    }

    if (!L.map || typeof L.map !== "function") {
        return null;
    }

    try {
        const iconConfigSuccess = configureLeafletMarkerIcons(L);
        if (!iconConfigSuccess) {
            console.warn("Marker icon konfigürasyonu başarısız oldu, map yine de oluşturuluyor");
        }

        const restrictToTurkey = options.restrictToTurkey !== false; // Varsayılan olarak true
        const turkeyBounds = L.latLngBounds(TURKEY_BOUNDS.southWest, TURKEY_BOUNDS.northEast);

        const {
            restrictToTurkey: _,
            minZoom,
            maxZoom,
            maxBounds,
            maxBoundsViscosity,
            ...userOptions
        } = options;

        const mapOptions = {
            ...userOptions,
            center: [center.lat, center.lng],
            zoom: zoom,
            zoomControl: userOptions.zoomControl !== undefined ? userOptions.zoomControl : true,
            attributionControl:
                userOptions.attributionControl !== undefined ? userOptions.attributionControl : true
        };

        // Türkiye sınırlarını zorunlu kıl - bu ayarlar ezilmemeli
        if (restrictToTurkey) {
            mapOptions.maxBounds = turkeyBounds;
            mapOptions.maxBoundsViscosity = 1.0; // Sınırlar dışına kesinlikle çıkmaz
            mapOptions.minZoom = minZoom !== undefined ? minZoom : 5.5; // Maksimum uzaklık sınırı
            mapOptions.maxZoom = maxZoom !== undefined ? maxZoom : 22;
        }

        // Map oluştur
        const map = L.map(mapContainer, mapOptions);

        // Ekstra güvenlik: moveend event'inde sınırları kontrol et
        if (restrictToTurkey) {
            map.on("moveend", function() {
                const currentCenter = map.getCenter();
                const currentBounds = map.getBounds();
                if (
                    !turkeyBounds.contains(currentCenter) ||
                    !turkeyBounds.contains(currentBounds)
                ) {
                    map.fitBounds(turkeyBounds, {animate: false, padding: [0, 0]});
                }
            });

            map.on("zoomend", function() {
                const currentBounds = map.getBounds();
                if (!turkeyBounds.contains(currentBounds)) {
                    map.fitBounds(turkeyBounds, {animate: false, padding: [0, 0]});
                }
            });
        }

        const preferredType = getTileLayerPreference();
        addTileLayerWithFallback(L, map, preferredType);

        return map;
    } catch (error) {
        console.error("Leaflet map oluşturulurken hata:", error);
        return null;
    }
};

export const getBundleIdAsDate = (bundleId, readableFormat = false) => {
    if (bundleId === "00000000-0000-0000-0000-000000000000" || !bundleId) {
        return null;
    }
    const timeHex = bundleId.replace(/-/g, "").substring(0, 12);
    const timestampMs = parseInt(timeHex, 16);
    return readableFormat ? moment(timestampMs).format("DD-MM-YYYY HH:mm:ss") : moment(timestampMs);
};

export const numericInputProps = {
    controls: false,
    parser: value => {
        if (!value) return "";
        const numericValue = value.replace(/\D/g, "");
        return numericValue;
    },
    formatter: value => {
        if (!value) return "";
        return value.replace(/\D/g, "");
    },
    onKeyPress: e => {
        if (
            !/[0-9]/.test(e.key) &&
            !["Backspace", "Delete", "Tab", "Enter", "ArrowLeft", "ArrowRight"].includes(e.key)
        ) {
            e.preventDefault();
        }
    }
};

/**
 * Nested object path'inden değer alır
 * @param {Object} obj - Kaynak obje
 * @param {string} path - Nokta ile ayrılmış path (örn: "info.brand")
 * @returns {*} Path'teki değer veya undefined
 */
export const getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;
    return path?.split?.(".")?.reduce?.((o, p) => o?.[p], obj);
};

/**
 * İki row arasında bir field'ın değişip değişmediğini kontrol eder
 * @param {Object} currentRow - Mevcut satır
 * @param {Object} previousRow - Önceki satır (null olabilir)
 * @param {string} fieldPath - Nokta ile ayrılmış field path (örn: "info.brand")
 * @returns {boolean} Field değiştiyse true, değilse false
 */
export const isFieldChanged = (currentRow, previousRow, fieldPath) => {
    if (!previousRow) return false;

    const currentValue = getNestedValue(currentRow, fieldPath);
    const previousValue = getNestedValue(previousRow, fieldPath);
    return currentValue !== previousValue;
};

export const formatLicensePlate = text => {
    // Büyük harfe çevir ve sadece geçerli karakterleri kabul et (yasaklı Türkçe karakterleri filtrele)
    const cleanText = (text || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .replace(/[ÇŞİÖÜĞQWX]/g, "");

    // Harf içerip içermediğini kontrol et
    const hasLetters = /[A-Z]/.test(cleanText);

    if (!hasLetters && cleanText.length <= 2) {
        // Sadece şehir kodu - sadece rakam kabul et
        const cityCode = cleanText.replace(/[^0-9]/g, "");
        // Şehir kodu validasyonu (1-81 arası olmalı)
        if (cityCode.length === 0) {
            return "";
        }
        const cityCodeNum = Number(cityCode);
        if (cityCode.length === 2 && cityCodeNum >= 1 && cityCodeNum <= 81) {
            return cityCode;
        } else if (cityCode.length === 1) {
            return cityCode;
        } else {
            return "";
        }
    } else {
        // Şehir kodu + harfler + rakamlar
        let cityCode = "";
        let restOfText = "";

        // İlk karakterin rakam olup olmadığını kontrol et
        const firstChar = cleanText.charAt(0);
        const secondChar = cleanText.charAt(1);

        if (firstChar && /[0-9]/.test(firstChar)) {
            // İlk karakter rakam
            if (cleanText.length >= 2 && secondChar && /[0-9]/.test(secondChar)) {
                // İki rakam var
                cityCode = cleanText.substring(0, 2);
                restOfText = cleanText.substring(2);
            } else {
                // Tek rakam var, ikinci karakter harf ise 0 ekle
                if (cleanText.length >= 2 && secondChar && /[A-Z]/.test(secondChar)) {
                    cityCode = "0" + firstChar;
                    restOfText = cleanText.substring(1);
                } else {
                    cityCode = firstChar;
                    restOfText = cleanText.substring(1);
                }
            }
        } else {
            // İlk karakter harf veya boş, geçersiz
            return "";
        }

        // Şehir kodunu sadece rakamlarla sınırla
        cityCode = cityCode.replace(/[^0-9]/g, "");
        if (cityCode.length < 2) {
            return cityCode;
        }

        // Şehir kodu validasyonu (1-81 arası olmalı)
        if (cityCode.length === 0) {
            return "";
        }
        const cityCodeNum = Number(cityCode);
        if (isNaN(cityCodeNum) || cityCodeNum < 1 || cityCodeNum > 81) {
            return "";
        }

        // Harfler ve rakamları ayır
        let letterPart = "";
        let numberPart = "";
        let foundNumber = false;

        for (let i = 0; i < restOfText.length; i++) {
            const char = restOfText[i];
            if (/[0-9]/.test(char)) {
                foundNumber = true;
                numberPart += char;
            } else if (/[A-Z]/.test(char) && !foundNumber) {
                letterPart += char;
            }
        }

        // Harfleri 1-3, rakamları 1-4 ile sınırla (Türk plaka formatı: 34 LA 8615)
        letterPart = letterPart.substring(0, 3);
        numberPart = numberPart.substring(0, 4);

        // En az 1 harf olmalı
        if (letterPart.length === 0) {
            return cityCode;
        }

        if (numberPart.length > 0) {
            return `${cityCode} ${letterPart} ${numberPart}`;
        } else {
            return `${cityCode} ${letterPart}`;
        }
    }
};

export const isImageUrl = url => {
    if (!url) return false;
    try {
        const pathname = new URL(url).pathname;
        const ext = pathname.split('.').pop().toLowerCase();
        return IMAGE_EXTENSIONS.includes(ext);
    } catch {
        const ext = url.split('?')[0].split('.').pop().toLowerCase();
        return IMAGE_EXTENSIONS.includes(ext);
    }
};
